import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';

const customTextStyles = {
  fontFamily: 'Manrope',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  letterSpacing: '-0.01em',
  textAlign: 'left',
};

const CancelButton = styled(Button)(({ theme }) => ({
  color: `var(--fi-button, ${theme?.primary || '#FF6A00'})`,
  backgroundColor: `var(--fi-bg1, ${
    theme?.background1 || (theme?.isDark ? '#121212' : 'white')
  })`,
  border: `2px solid var(--fi-button, ${theme?.primary || '#FF6A00'})`,
  '&:hover': {
    backgroundColor: `var(--fi-button, ${theme?.primary || '#FF6A00'})`,
    color: `var(--fi-button-text, ${theme?.textCTA || 'white'})`,
  },
  width: '50%',
  marginRight: '16px',
  paddingTop: '10px',
  paddingBottom: '10px',
  ...customTextStyles,
}));

const SaveButton = styled(Button)(({ theme }) => ({
  color: `var(--fi-button-text, ${theme?.textCTA || 'white'})`,
  backgroundColor: `var(--fi-button, ${theme?.primary || '#FF6A00'})`,
  border: `2px solid var(--fi-button, ${theme?.primary || '#FF6A00'})`,
  '&:hover': {
    backgroundColor: `var(--fi-button-hover, ${
      theme?.primaryDark || '#DC5C01'
    })`,
    borderColor: `var(--fi-button-hover, ${theme?.primaryDark || '#DC5C01'})`,
    color: `var(--fi-button-text, ${theme?.textCTA || 'white'})`,
  },
  width: '50%',
  paddingTop: '10px',
  paddingBottom: '10px',
  ...customTextStyles,
}));

const ConfirmationModal = ({
  open,
  onClose,
  message,
  onConfirm,
  title = 'Save',
  confirmationButtonText,
  isLoading,
}) => {
  const { theme } = useTheme();
  const isDark = theme.isDark;

  const titleStyle = {
    fontFamily: 'Manrope',
    fontSize: '28px',
    fontWeight: '600px',
    lineHeight: '34px',
    letterSpacing: '0em',
    textAlign: 'left',
    color: theme?.text || (isDark ? '#FFFFFF' : '#222428'),
    marginBottom: '0px',
  };

  const subtitleStyle = {
    fontFamily: 'Source Sans Pro',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '-0.01em',
    textAlign: 'left',
    color: theme?.textLight || (isDark ? '#B0B0B0' : '#868686'),
  };

  const modalContainerStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 624,
    backgroundColor: theme?.background1 || (isDark ? '#121212' : 'white'),
    boxShadow: isDark
      ? '0px 4px 24px rgba(0, 0, 0, 0.6)'
      : '0px 4px 24px rgba(0, 0, 0, 0.1)',
    p: 4,
    borderRadius: '16px',
    padding: '40px',
    border: isDark ? `1px solid ${theme?.stroke || '#404040'}` : 'none',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '16px',
    right: '16px',
    color: theme?.text || (isDark ? '#FFFFFF' : '#222428'),
    '&:hover': {
      backgroundColor: theme?.background2 || (isDark ? '#1E1E1E' : '#f5f5f5'),
    },
  };

  return (
    <Modal open={open} onClose={onClose} style={{ zIndex: 100002 }}>
      <Box style={modalContainerStyle}>
        <IconButton style={closeButtonStyle} onClick={onClose}>
          <Close />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <h1 style={titleStyle}>{title || 'Are you sure?'}</h1>
        </Box>
        <p style={subtitleStyle}>{message}</p>
        <Box sx={{ display: 'flex', marginTop: 5 }}>
          <CancelButton variant="contained" onClick={onClose} theme={theme}>
            Cancel
          </CancelButton>
          <SaveButton
            variant="contained"
            color="error"
            onClick={onConfirm}
            theme={theme}
          >
            {isLoading ? (
              <CircularProgress
                size={24}
                sx={{
                  color: `var(--fi-button-text, ${theme?.textCTA || 'white'})`,
                }}
              />
            ) : (
              confirmationButtonText
            )}
          </SaveButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfirmationModal;
